import { useEffect, useRef } from 'react'
import { useClient, useMutation } from '@truepill/tpos-react-router'
import { ADD_ORDER_USER_EVENT } from 'gql'
import { makeKeepAliveGraphQLRequest } from 'utils/keepaliveGraphqlRequest'

type UserEventTimerProps = {
  event: string
  orderId: string
  rxFillRequestId?: string
}

/**
 * This component will start a new event every time it mounts, then it will call the addEvent endpoint
 * when either the component is unmounted or the user leaves the page (tab/window closed)
 * @param
 * @returns
 */
export default function UserEventTimer({ event, orderId, rxFillRequestId }: UserEventTimerProps): JSX.Element {
  const [addUserEvent] = useMutation(ADD_ORDER_USER_EVENT)
  const currentEvent = useRef({
    event,
    orderId,
    rxFillRequestId,
    startAt: new Date(),
  })
  const { token } = useClient()

  /**
   * This hook handles the pagehide, so we can keep track of current event even when the user closes the tab/window.
   * We use fetch + keepalive instead of the useMutation hook to support sending it after the tab/window is closed.
   */
  useEffect(() => {
    const handler = (_event: PageTransitionEvent) => {
      if (_event.persisted) {
        return
      }

      makeKeepAliveGraphQLRequest({
        operationName: 'addUserEvent',
        variables: {
          orderId,
          rxFillRequestId,
          userEvent: {
            event,
            startAt: currentEvent.current.startAt,
            endAt: new Date(),
          },
        },
        query: ADD_ORDER_USER_EVENT,
        token,
      })
    }

    window.addEventListener('pagehide', handler)
    return () => {
      window.removeEventListener('pagehide', handler)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only run on mount
  }, [])

  /**
   * This hook sets the start of the event on mount, then
   * sends the event on unmount (not executed when closing tab/windows, the previous hook handles that case)
   */
  useEffect(() => {
    const startAt = currentEvent.current.startAt

    return function cleanupEventTimer() {
      const endAt = new Date()

      addUserEvent({
        variables: {
          orderId,
          rxFillRequestId,
          userEvent: {
            event,
            startAt,
            endAt,
          },
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only run on mount/unmount
  }, [])

  return <></>
}
